/**
 * A small DC circuit solver using Modified Nodal Analysis (the approach SPICE uses),
 * specialised for kid-kit circuits:
 *
 *  - Every element is a conductance, a current source, or a diode.
 *  - Voltage sources (batteries, motor back-EMF) are stamped as Norton equivalents,
 *    so the system is a pure nodal matrix with no extra branch rows.
 *  - Every node has a tiny leak to ground (GMIN), so floating parts and disconnected
 *    sub-circuits never make the matrix singular.
 *  - Diodes are solved with Newton–Raphson plus SPICE-style junction voltage limiting.
 *
 * Node indices are 0..n-1; ground is implicit.
 */

export const GMIN = 1e-9;
const DIODE_GMIN = 1e-12;
const MAX_ITER = 100;
const VTOL = 1e-7;

export interface Conductance {
  kind: 'g';
  a: number;
  b: number;
  g: number;
}

/** A current source: `i` amps leave node `a` through the source and enter node `b`. */
export interface CurrentSource {
  kind: 'i';
  a: number;
  b: number;
  i: number;
}

/** Ideal-ish junction diode, anode `a`, cathode `b`: I = Is·(exp(Vab / nVt) − 1). */
export interface Diode {
  kind: 'd';
  a: number;
  b: number;
  is: number;
  nvt: number;
}

export type Element = Conductance | CurrentSource | Diode;

export interface Solution {
  /** Node voltages relative to ground. */
  v: Float64Array;
  iterations: number;
  converged: boolean;
}

export const diodeCurrent = (d: Diode, vd: number): number => d.is * (Math.exp(Math.min(vd / d.nvt, 80)) - 1);

/**
 * Solve the circuit. `guess` (e.g. last tick's solution) makes Newton converge in 1–3 steps.
 * Independent sub-circuits (separate loops on the board) are solved separately, which keeps
 * big boards fast: cost grows with the largest loop, not the whole board.
 */
export function solve(nodeCount: number, elements: readonly Element[], guess?: Float64Array): Solution {
  const v = new Float64Array(nodeCount);
  if (guess && guess.length === nodeCount) v.set(guess);
  if (nodeCount === 0) return { v, iterations: 0, converged: true };

  // Union-find over nodes to split the circuit into independent components.
  const parent = new Int32Array(nodeCount).map((_, i) => i);
  const find = (x: number): number => {
    while (parent[x] !== x) x = parent[x] = parent[parent[x]!]!;
    return x;
  };
  for (const e of elements) if (e.a >= 0 && e.b >= 0) parent[find(e.a)] = find(e.b);

  const groups = new Map<number, { nodes: number[]; elements: Element[] }>();
  for (let k = 0; k < nodeCount; k++) {
    const r = find(k);
    let g = groups.get(r);
    if (!g) groups.set(r, (g = { nodes: [], elements: [] }));
    g.nodes.push(k);
  }
  for (const e of elements) {
    const node = e.a >= 0 ? e.a : e.b;
    if (node >= 0) groups.get(find(node))!.elements.push(e);
  }

  let iterations = 0;
  let converged = true;
  const local = new Int32Array(nodeCount);
  for (const g of groups.values()) {
    if (!g.elements.length) {
      for (const k of g.nodes) v[k] = 0;
      continue;
    }
    g.nodes.forEach((k, i) => (local[k] = i));
    const map = (n: number) => (n < 0 ? -1 : local[n]!);
    const els = g.elements.map((e) => ({ ...e, a: map(e.a), b: map(e.b) }));
    const sub = new Float64Array(g.nodes.length);
    g.nodes.forEach((k, i) => (sub[i] = v[k]!));
    const r = solveComponent(g.nodes.length, els, sub);
    g.nodes.forEach((k, i) => (v[k] = r.v[i]!));
    iterations = Math.max(iterations, r.iterations);
    converged &&= r.converged;
  }
  return { v, iterations, converged };
}

function solveComponent(n: number, elements: readonly Element[], v: Float64Array): Solution {
  const diodes = elements.filter((e): e is Diode => e.kind === 'd');
  // Last junction voltage per diode, for voltage limiting.
  const vdLast = diodes.map((d) => volt(v, d.a) - volt(v, d.b));

  const A = new Float64Array(n * n);
  const z = new Float64Array(n);

  let iterations = 0;
  let converged = false;
  const maxIter = diodes.length ? MAX_ITER : 1;

  for (; iterations < maxIter; iterations++) {
    A.fill(0);
    z.fill(0);
    for (let k = 0; k < n; k++) A[k * n + k] = GMIN;

    for (const e of elements) {
      if (e.kind === 'g') stampG(A, n, e.a, e.b, e.g);
      else if (e.kind === 'i') stampI(z, e.a, e.b, e.i);
    }
    diodes.forEach((d, idx) => {
      const vd = vdLast[idx]!;
      const ex = Math.exp(Math.min(vd / d.nvt, 80));
      const id = d.is * (ex - 1);
      const gd = (d.is / d.nvt) * ex + DIODE_GMIN;
      // Companion model: conductance gd in parallel with a current source (id − gd·vd), a → b.
      stampG(A, n, d.a, d.b, gd);
      stampI(z, d.a, d.b, id - gd * vd);
    });

    const next = gaussSolve(A, z, n);

    if (!diodes.length) {
      v.set(next);
      converged = true;
      iterations = 1;
      break;
    }

    // Limit junction voltages, then check convergence.
    let maxDelta = 0;
    for (let k = 0; k < n; k++) maxDelta = Math.max(maxDelta, Math.abs(next[k]! - v[k]!));
    let limited = false;
    diodes.forEach((d, idx) => {
      const vdNew = volt(next, d.a) - volt(next, d.b);
      const vdLim = pnjlim(vdNew, vdLast[idx]!, d.nvt, d.is);
      if (vdLim !== vdNew) limited = true;
      vdLast[idx] = vdLim;
    });
    v.set(next);
    if (!limited && maxDelta < VTOL) {
      converged = true;
      iterations++;
      break;
    }
  }
  return { v, iterations, converged };
}

export const volt = (v: Float64Array, node: number): number => (node < 0 ? 0 : v[node]!);

function stampG(A: Float64Array, n: number, a: number, b: number, g: number): void {
  if (a >= 0) A[a * n + a]! += g;
  if (b >= 0) A[b * n + b]! += g;
  if (a >= 0 && b >= 0) {
    A[a * n + b]! -= g;
    A[b * n + a]! -= g;
  }
}

function stampI(z: Float64Array, a: number, b: number, i: number): void {
  if (a >= 0) z[a]! -= i;
  if (b >= 0) z[b]! += i;
}

/** SPICE's pn-junction voltage limiter: stops Newton from overshooting into exp() overflow. */
function pnjlim(vnew: number, vold: number, vt: number, is: number): number {
  const vcrit = vt * Math.log(vt / (Math.SQRT2 * is));
  if (vnew > vcrit && Math.abs(vnew - vold) > 2 * vt) {
    if (vold > 0) {
      const arg = 1 + (vnew - vold) / vt;
      return arg > 0 ? vold + vt * Math.log(arg) : vcrit;
    }
    return vt * Math.log(vnew / vt);
  }
  return vnew;
}

/** Dense Gaussian elimination with partial pivoting. Boards have < ~100 nodes, so this is plenty. */
export function gaussSolve(Ain: Float64Array, zin: Float64Array, n: number): Float64Array {
  const A = Float64Array.from(Ain);
  const z = Float64Array.from(zin);
  for (let c = 0; c < n; c++) {
    let p = c;
    let best = Math.abs(A[c * n + c]!);
    for (let r = c + 1; r < n; r++) {
      const m = Math.abs(A[r * n + c]!);
      if (m > best) {
        best = m;
        p = r;
      }
    }
    if (best === 0) continue;
    if (p !== c) {
      for (let k = 0; k < n; k++) {
        const t = A[c * n + k]!;
        A[c * n + k] = A[p * n + k]!;
        A[p * n + k] = t;
      }
      const t = z[c]!;
      z[c] = z[p]!;
      z[p] = t;
    }
    const piv = A[c * n + c]!;
    for (let r = c + 1; r < n; r++) {
      const f = A[r * n + c]! / piv;
      if (f === 0) continue;
      for (let k = c; k < n; k++) A[r * n + k]! -= f * A[c * n + k]!;
      z[r]! -= f * z[c]!;
    }
  }
  const x = new Float64Array(n);
  for (let r = n - 1; r >= 0; r--) {
    let s = z[r]!;
    for (let k = r + 1; k < n; k++) s -= A[r * n + k]! * x[k]!;
    const d = A[r * n + r]!;
    x[r] = d === 0 ? 0 : s / d;
  }
  return x;
}
